// One Filters button with a panel of drop-downs and tick boxes, the same panel as the Time & Action Plan's, for screens
// that draw their own filter bar (Sampling, CAD Library, Artwork Library, QC, Shipment Control, Claims, Deals…). The
// controls inside keep their own data-change / data-action handlers; only where they sit changes. Lists built with
// mountTable (web/src/table.ts) have the same panel built in.
import { esc } from "./lib.js";
import { onAction } from "./state.js";

/** Panels left open, so a redraw after a change keeps the panel open (closed by Done, a click outside or Escape). */
const open = new Set<string>();

export interface FilterPanel {
  /** A short id for this screen's panel, e.g. "sampling". */
  id: string;
  /** The controls: each drop-down in a <label class="std-fp-field"><span>Label</span><select …></label>, tick boxes as
   *  <label class="check-row cp-fp-row">. */
  fields: string;
  /** How many choices differ from the standard (shown on the button). */
  changed: number;
  /** "12 of 30 shown" or similar, at the foot of the panel. */
  shown?: string;
  /** data-action that puts every filter back to the standard; no Reset link without it. */
  resetAction?: string;
}

export function filterPanelHtml(o: FilterPanel): string {
  const isOpen = open.has(o.id);
  return `<span class="cp-filter-wrap std-filter-wrap" data-fp="${esc(o.id)}">
  <button type="button" class="secondary small-btn cp-filter-btn ${isOpen ? "is-open" : ""}" data-action="std-filters" data-fp="${esc(o.id)}" aria-haspopup="dialog" aria-expanded="${isOpen}"><span class="cp-funnel" aria-hidden="true"></span>Filters<span class="cp-filter-n" ${o.changed ? "" : "hidden"}>${o.changed}</span><span class="cp-caret" aria-hidden="true"></span></button>
  <span class="cp-filter-shade" data-action="std-filters-close" data-fp="${esc(o.id)}" ${isOpen ? "" : "hidden"}></span>
  <div class="cp-filter-pop std-filter-pop" role="dialog" aria-label="Filters" ${isOpen ? "" : "hidden"}>
    <div class="cp-fp-head"><h4>Filters</h4>${o.resetAction ? `<button type="button" class="link-btn" data-action="${esc(o.resetAction)}">Reset to standard</button>` : ""}</div>
    <div class="cp-fp-sec std-fp-fields">${o.fields}</div>
    <div class="cp-fp-foot"><span class="cp-fp-hint">${esc(o.shown || "")}</span><button type="button" class="primary small-btn" data-action="std-filters-close" data-fp="${esc(o.id)}">Done</button></div>
  </div></span>`;
}

function setOpen(id: string, on: boolean) {
  if (on) open.add(id); else open.delete(id);
  document.querySelectorAll<HTMLElement>(`.std-filter-wrap[data-fp="${CSS.escape(id)}"]`).forEach((w) => {
    w.querySelectorAll<HTMLElement>(".std-filter-pop, .cp-filter-shade").forEach((el) => { el.hidden = !on; });
    const b = w.querySelector<HTMLElement>(".cp-filter-btn");
    b?.classList.toggle("is-open", on);
    b?.setAttribute("aria-expanded", String(on));
  });
}

onAction("std-filters", (el) => { const id = el.dataset.fp || ""; setOpen(id, !open.has(id)); });
onAction("std-filters-close", (el) => setOpen(el.dataset.fp || "", false));
document.addEventListener("click", (e) => {
  if (!open.size) return;
  const t = e.target as HTMLElement;
  if (!t.isConnected || t.closest(".std-filter-wrap, #modal, .confirm-box")) return;
  for (const id of [...open]) setOpen(id, false);
});
document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape" || !open.size) return;
  const id = [...open][0];
  for (const x of [...open]) setOpen(x, false);
  document.querySelector<HTMLElement>(`.std-filter-wrap[data-fp="${CSS.escape(id)}"] .cp-filter-btn`)?.focus();
});
